import {useState, useRef, useEffect} from 'react';
import {useAuth} from '@common/auth/use-auth';
import {UserAvatar} from '@common/auth/user-avatar';
import clsx from 'clsx';

interface Account {
  email: string;
  name: string;
  image?: string;
  isLoggedIn?: boolean;
  user_id?: string;
}

interface ApiAccount {
  user_id: string;
  identifier: string;
  active: boolean;
}

const ACCOUNTS_BACKEND_URL = import.meta.env.VITE_ACCOUNTS_BACKEND_URL || 'https://account.indzs.com';
const ACCOUNTS_FRONTEND_URL = import.meta.env.VITE_ACCOUNTS_FRONTEND_URL || 'https://account.indzs.com';

export function AccountSwitcher() {
  const {user} = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [showMore, setShowMore] = useState(true);
  const [mergedAccounts, setMergedAccounts] = useState<Account[]>([]);
  const [fetchingAccounts, setFetchingAccounts] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<string | null>(null);
  const [switchingId, setSwitchingId] = useState<string | null>(null);
  const [removingEmail, setRemovingEmail] = useState<string | null>(null);
  const [reloadAccounts, setReloadAccounts] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);

  const getDeviceId = () => {
    if (typeof window === 'undefined') return '';
    let deviceId = localStorage.getItem('device_id');
    if (!deviceId) {
      deviceId =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : Math.random().toString(36).substring(2) + Date.now().toString(36);
      localStorage.setItem('device_id', deviceId);
    }
    return deviceId;
  };

  const handleLogOut = () => {
    setIsOpen(false);
    window.location.href = '/auth/accounts/global-logout';
  };

  const handleSwitchAccount = async (userId: string) => {
    const deviceId = getDeviceId();
    setSwitchingId(userId);
    try {
      const response = await fetch(
        `${ACCOUNTS_BACKEND_URL}/api/auth/switch-account/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Device-Id': deviceId,
          },
          credentials: 'include',
          body: JSON.stringify({user_id: userId}),
        },
      );
      if (response.ok) {
        window.location.href = '/auth/accounts/redirect?prompt=none';
      }
    } catch (error) {
      console.error('Failed to switch account', error);
    } finally {
      setSwitchingId(null);
    }
  };

  const handleRemoveAccount = async (e: React.MouseEvent, acc: Account) => {
    e.stopPropagation();
    setRemovingEmail(acc.email);

    try {
      if (acc.isLoggedIn && acc.user_id) {
        const deviceId = getDeviceId();
        await fetch(`${ACCOUNTS_BACKEND_URL}/api/auth/account/delete/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Device-Id': deviceId,
          },
          credentials: 'include',
          body: JSON.stringify({user_id: acc.user_id}),
        });
        setReloadAccounts(prev => !prev);
        return;
      }

      const saved = localStorage.getItem('saved_accounts');
      if (saved) {
        const localAccounts: Account[] = JSON.parse(saved);
        const updated = localAccounts.filter(a => a.email !== acc.email);
        localStorage.setItem('saved_accounts', JSON.stringify(updated));
      }

      setMergedAccounts(prev => prev.filter(a => a.email !== acc.email));
      setSelectedEmail(null);
    } catch (error) {
      console.error('Error removing account', error);
    } finally {
      setRemovingEmail(null);
    }
  };

  useEffect(() => {
    const syncAccounts = async () => {
      const deviceId = getDeviceId();
      setFetchingAccounts(true);
      try {
        const res = await fetch(`${ACCOUNTS_BACKEND_URL}/api/auth/accounts/`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-Device-Id': deviceId,
          },
          credentials: 'include',
        });
        if (res.ok) {
          const apiAccounts: ApiAccount[] = await res.json();
          const currentEmail = user?.email;

          const saved = localStorage.getItem('saved_accounts');
          let localAccounts: Account[] = saved ? JSON.parse(saved) : [];

          const otherApiAccounts = apiAccounts
            .filter(api => api.identifier !== currentEmail)
            .map(api => ({
              email: api.identifier,
              name: api.identifier.split('@')[0],
              user_id: api.user_id,
              isLoggedIn: true,
            }));

          const filteredLocal = localAccounts
            .filter(
              local =>
                local.email !== currentEmail &&
                !apiAccounts.some(api => api.identifier === local.email),
            )
            .map(local => ({...local, isLoggedIn: false}));

          setMergedAccounts([...otherApiAccounts, ...filteredLocal]);
        }
      } catch (e) {
        console.error('Error syncing accounts', e);
      } finally {
        setFetchingAccounts(false);
      }
    };

    if (isOpen && user) {
      syncAccounts();
    }
  }, [isOpen, user, reloadAccounts]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSelectedEmail(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!user) return null;

  const u = user as any;
  const rawEmail = u?.primary_email || u?.email || (u?.username?.includes('@') ? u?.username : '');
  const displayEmail = rawEmail || (u?.username ? `${u?.username}@indzs.com` : 'Account');

  const rawName = u?.first_name || u?.name || (u?.username && !u?.username.includes('@') ? u?.username : u?.username?.split('@')[0]);
  const displayName = rawName || displayEmail.split('@')[0] || 'User';

  return (
    <div className="relative flex items-center" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-8 rounded-full border border-divider p-2 hover:bg-hover transition-colors cursor-pointer"
        aria-label="Account switcher"
      >
        <UserAvatar user={user} circle size="w-32 h-32" />
        <span className="hidden md:block max-w-124 truncate text-sm font-medium pr-4">
          {displayName}
        </span>
      </button>

      {isOpen && (
        <div className="absolute top-48 right-0 z-50 w-[350px] max-h-[80vh] overflow-y-auto rounded-[28px] bg-[#f0f4f9] dark:bg-[#1e1f20] p-8 text-main shadow-2xl border border-divider">
          {/* Main User Card */}
          <div className="relative rounded-[24px] bg-bg-alt p-16 text-center shadow-sm">
            <button
              onClick={() => setIsOpen(false)}
              className="absolute top-12 right-12 text-muted hover:bg-hover p-6 rounded-full transition-colors cursor-pointer"
            >
              <svg className="w-18 h-18 fill-current" viewBox="0 0 24 24">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>
            <p className="mb-16 text-sm font-medium text-muted break-all">
              {displayEmail}
            </p>
            <div className="relative mx-auto w-80 h-80 flex justify-center">
              <UserAvatar user={user} circle size="w-80 h-80 text-3xl" />
              <a
                href={`${ACCOUNTS_FRONTEND_URL}/settings`}
                className="absolute bottom-0 right-0 rounded-full border border-divider bg-bg-alt p-6 shadow-md hover:bg-hover text-main"
              >
                <svg className="w-14 h-14 fill-current" viewBox="0 0 24 24">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
              </a>
            </div>
            <h2 className="mt-12 text-xl font-normal text-main">
              Hi, {displayName}!
            </h2>
            <a href={`${ACCOUNTS_FRONTEND_URL}/settings`}>
              <button className="mt-16 rounded-full border border-primary px-24 py-8 text-sm font-semibold text-primary hover:bg-primary/10 transition-colors cursor-pointer">
                Manage your Account
              </button>
            </a>
          </div>

          {/* Other Accounts Section */}
          <div className="mt-8 rounded-[24px] bg-bg-alt shadow-sm overflow-hidden">
            <button
              onClick={() => setShowMore(!showMore)}
              className="flex w-full items-center justify-between p-16 hover:bg-hover transition-colors cursor-pointer"
            >
              <span className="text-sm font-medium">Other accounts</span>
              <svg className="w-18 h-18 fill-current" viewBox="0 0 24 24">
                {showMore ? (
                  <path d="M12 8l-6 6 1.41 1.41L12 10.83l4.59 4.58L18 14z" />
                ) : (
                  <path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" />
                )}
              </svg>
            </button>

            {showMore && (
              <div className="pb-8">
                {fetchingAccounts ? (
                  <div className="space-y-16 p-16">
                    {[1, 2].map(i => (
                      <div
                        key={i}
                        className="flex items-center gap-12 animate-pulse"
                      >
                        <div className="h-36 w-36 rounded-full bg-muted" />
                        <div className="flex-1 space-y-8">
                          <div className="h-12 w-1/2 rounded bg-muted" />
                          <div className="h-10 w-3/4 rounded bg-muted" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  mergedAccounts.map(acc => (
                    <div
                      key={acc.email}
                      onClick={() => {
                        if (switchingId || removingEmail) return;
                        if (acc.isLoggedIn && acc.user_id) {
                          handleSwitchAccount(acc.user_id);
                        } else {
                          window.location.href = `${ACCOUNTS_FRONTEND_URL}/login?email=${encodeURIComponent(
                            acc.email,
                          )}`;
                        }
                      }}
                      className={clsx(
                        'group relative flex items-center justify-between border-t border-divider px-16 py-12 hover:bg-hover cursor-pointer transition-colors',
                        selectedEmail === acc.email && 'bg-hover',
                        switchingId === acc.user_id &&
                          'opacity-70 pointer-events-none',
                      )}
                    >
                      <div className="flex items-center gap-12">
                        <div className="flex h-36 w-36 items-center justify-center rounded-full bg-muted text-sm font-bold text-muted overflow-hidden">
                          {acc.image ? (
                            <img
                              src={acc.image}
                              alt=""
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            acc.name.charAt(0).toUpperCase()
                          )}
                        </div>
                        <div className="text-left">
                          <p className="text-sm font-semibold leading-none text-main">
                            {acc.name}
                          </p>
                          <p className="mt-4 text-xs text-muted break-all">
                            {acc.email}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-8">
                        {!acc.isLoggedIn && (
                          <span className="rounded bg-muted px-6 py-2 text-[10px] text-muted">
                            Signed out
                          </span>
                        )}

                        <button
                          onClick={e => handleRemoveAccount(e, acc)}
                          className={clsx(
                            'rounded-full p-6 text-muted hover:bg-danger/10 hover:text-danger transition-all opacity-0 group-hover:opacity-100 cursor-pointer',
                            selectedEmail === acc.email &&
                              'opacity-100 bg-danger/10 text-danger scale-110',
                            removingEmail === acc.email && 'opacity-100',
                          )}
                          title="Remove account"
                        >
                          <svg
                            className="w-16 h-16 fill-current"
                            viewBox="0 0 24 24"
                          >
                            <path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2h6v2H6zm0 4h12v2H6z" />
                          </svg>
                        </button>

                        <svg
                          className={clsx(
                            'w-16 h-16 fill-current text-muted transition-opacity',
                            selectedEmail === acc.email
                              ? 'opacity-100'
                              : 'opacity-0 group-hover:opacity-100',
                          )}
                          viewBox="0 0 24 24"
                        >
                          <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                        </svg>
                      </div>
                    </div>
                  ))
                )}

                <button
                  onClick={() => {
                    window.location.href = '/auth/accounts/redirect?prompt=select_account';
                  }}
                  className="flex w-full items-center gap-12 border-t border-divider px-16 py-12 hover:bg-hover text-main transition-colors cursor-pointer"
                >
                  <div className="flex h-36 w-36 items-center justify-center">
                    <svg className="w-20 h-20 fill-current" viewBox="0 0 24 24">
                      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium">Switch account</span>
                </button>

                <button
                  onClick={handleLogOut}
                  className="flex w-full items-center gap-12 border-t border-divider px-16 py-12 hover:bg-hover text-main transition-colors cursor-pointer"
                >
                  <div className="flex h-36 w-36 items-center justify-center">
                    <svg className="w-20 h-20 fill-current" viewBox="0 0 24 24">
                      <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
                    </svg>
                  </div>
                  <span className="text-sm font-medium">Sign out</span>
                </button>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-center gap-16 py-16 text-[10px] font-medium uppercase tracking-tighter text-muted">
            <a
              href={`${ACCOUNTS_FRONTEND_URL}/help`}
              className="hover:underline"
            >
              Help
            </a>
            <span>•</span>
            <a
              href={`${ACCOUNTS_FRONTEND_URL}/privacy`}
              className="hover:underline"
            >
              Privacy
            </a>
            <span>•</span>
            <a
              href={`${ACCOUNTS_FRONTEND_URL}/terms`}
              className="hover:underline"
            >
              Terms
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
